<script setup lang="ts">
    import {
        EditableArea,
        EditableCancelTrigger,
        EditableEditTrigger,
        EditableInput,
        EditablePreview,
        EditableRoot,
        EditableSubmitTrigger,
    } from 'reka-ui';
    import { Button } from '@/components/ui/button';
    import { Check, Globe, Lock, Pencil, X } from 'lucide-vue-next';
    import { useI18n } from 'vue-i18n';
    import type { UserTableViewOption } from '@/types';
    import { useViewOptionsStore } from '@/stores/view-options';
    import ViewOptionDelete from '@/components/ViewOptionDelete.vue';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import { computed, ref } from 'vue';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';

    interface Props {
        viewOption: UserTableViewOption;
    }
    const props = defineProps<Props>();

    const { t } = useI18n();

    const name = ref(props.viewOption.name);

    const viewOptionsStore = useViewOptionsStore();
    const { hasPermission } = usePermissions();

    // Only the owner of a (non-default) view may rename, delete or share it.
    const canManage = computed(() => props.viewOption.is_owner && !props.viewOption.is_default);
    const canShareGlobal = computed(() => hasPermission(Permission.DASHBOARD_ADMIN_SHARE_GLOBAL));

    const isActive = computed(
        () => viewOptionsStore.activeViewOption?.id === props.viewOption.id,
    );

    const displayName = computed(() =>
        props.viewOption.is_default
            ? t('datatables.view_options.default')
            : props.viewOption.name || t('datatables.view_options.placeholder'),
    );

    const mutation = useMutation({
        mutationFn: async () => {
            return await axios.patch(
                route('v2.dashboard.view-options.update', props.viewOption.id),
                { name: name.value },
            );
        },
        onSuccess: () => {
            viewOptionsStore.updateViewOption(props.viewOption.id, name.value);
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const updateState = (state: 'cancel' | 'submit' | 'edit') => {
        viewOptionsStore.setState(state);
        viewOptionsStore.removeDefaultEditing(props.viewOption.id);
    };

    const toggleGlobal = () => {
        viewOptionsStore.toggleGlobal(props.viewOption);
    };
</script>

<template>
    <EditableRoot
        v-if="canManage"
        v-slot="{ isEditing }"
        v-model="name"
        :placeholder="t('datatables.view_options.placeholder')"
        class="flex w-full items-center gap-2 px-2"
        submit-mode="enter"
        activation-mode="none"
        @submit="mutation.mutate()"
        :start-with-edit-mode="viewOption.default_editing"
        @update:state="updateState"
    >
        <EditableArea class="text-foreground w-full text-xs">
            <EditablePreview
                @click="viewOptionsStore.setActive(viewOption)"
                class="hover:text-primary flex h-7 cursor-pointer items-center"
                :class="{ 'font-bold': isActive }"
            />
            <EditableInput
                class="placeholder:text-faded-foreground w-full py-1 outline-none placeholder:text-xs"
            />
        </EditableArea>
        <div
            v-if="!isEditing"
            class="flex"
        >
            <Button
                v-if="canShareGlobal || viewOption.is_global"
                :variant="viewOption.is_global ? 'continue' : 'ghost'"
                size="icon_sm"
                :class="
                    viewOption.is_global
                        ? undefined
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                "
                :title="
                    viewOption.is_global
                        ? t('datatables.view_options.make_personal')
                        : t('datatables.view_options.make_global')
                "
                @click="toggleGlobal"
            >
                <Globe />
            </Button>
            <span
                v-else
                class="size-7 shrink-0"
                aria-hidden="true"
            />
            <EditableEditTrigger as-child>
                <Button
                    variant="edit"
                    size="icon_sm"
                >
                    <Pencil />
                </Button>
            </EditableEditTrigger>
            <ViewOptionDelete :view-option-id="viewOption.id" />
        </div>
        <div
            v-else
            class="flex"
        >
            <EditableSubmitTrigger as-child>
                <Button
                    variant="accent"
                    size="icon_sm"
                >
                    <Check />
                </Button>
            </EditableSubmitTrigger>
            <EditableCancelTrigger as-child>
                <Button
                    variant="delete"
                    size="icon_sm"
                >
                    <X />
                </Button>
            </EditableCancelTrigger>
        </div>
    </EditableRoot>

    <div
        v-else
        class="flex w-full items-center gap-2 px-2"
    >
        <span
            @click="viewOptionsStore.setActive(viewOption)"
            class="text-foreground hover:text-primary flex h-7 min-w-0 flex-1 cursor-pointer items-center text-xs"
            :class="{ 'font-bold': isActive }"
        >
            {{ displayName }}
        </span>
        <div class="flex shrink-0">
            <span
                class="size-7 shrink-0"
                aria-hidden="true"
            />
            <span
                class="size-7 shrink-0"
                aria-hidden="true"
            />
            <Button
                variant="ghost"
                size="icon_sm"
                disabled
                tabindex="-1"
                class="text-muted-foreground pointer-events-none opacity-100"
                :title="
                    viewOption.is_default
                        ? t('datatables.view_options.default')
                        : t('datatables.view_options.shared')
                "
            >
                <Lock v-if="viewOption.is_default" />
                <Globe v-else />
            </Button>
        </div>
    </div>
</template>
